import React, {useEffect} from 'react';
import {
    createStyles,
    Header,
    Group,
    ActionIcon,
    Container,
    Burger,
    rem,
    Grid,
    Box,
    Transition, Card,
} from '@mantine/core';
import {useDisclosure, useMediaQuery, useWindowScroll} from '@mantine/hooks';
import {
    IconBrandTwitter,
    IconBrandYoutube,
    IconBrandInstagram,
    IconHome,
    IconUser, IconSearch, IconAddressBook, IconLicense,
} from '@tabler/icons-react';

import settings from '../settings.json';
import {spotlight} from "@mantine/spotlight";

const useStyles = createStyles((theme) => ({
    inner: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: rem(70),

        [theme.fn.smallerThan('sm')]: {
            justifyContent: 'flex-start',
        },
    },

    links: {
        width: rem(260),

        [theme.fn.smallerThan('sm')]: {
            display: 'none',
        },
    },

    social: {
        width: rem(260),

        [theme.fn.smallerThan('sm')]: {
            width: 'auto',
            marginLeft: 'auto',
        },
    },

    burger: {
        marginRight: theme.spacing.md,
        zIndex: 1001,

        [theme.fn.largerThan('sm')]: {
            display: 'none',
        },
    },

    burgerFullscreen: {
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        marginRight: theme.spacing.xs,
    },

    link: {
        display: 'block',
        lineHeight: 1,
        padding: `${rem(8)} ${rem(12)}`,
        borderRadius: theme.radius.sm,
        textDecoration: 'none',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        fontSize: theme.fontSizes.md,
        fontWeight: 500,

        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        },
    },

    linkActive: {
        '&, &:hover': {
            backgroundColor: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background,
            color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color,
        },
    },

    fullScreenNav: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        height: '100vh',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
        zIndex: 1000,
    },
    fullNavItem: {
        padding: theme.spacing.md,
        fontSize: theme.fontSizes.md,
        fontWeight: 500,
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        textDecoration: 'none',
        display: 'block',
        borderRadius: theme.radius.sm,
    },
    smallOverlay: {
        position: 'fixed',
        top: -8,
        left: 0,
        right: 0,
        height: rem(70),
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `0 ${theme.spacing.md}px`,
    },
}));

interface HeaderMiddleProps {
    links: { link: string; label: string }[];
}

// @ts-ignore
export default function MyHeader({ links, active }: HeaderMiddleProps) {
    const [opened, { toggle }] = useDisclosure(false);
    const { classes, cx } = useStyles();
    const [openedSmallOverlay, { toggle: toggleSmallOverlay }] = useDisclosure(false);

    const isMobile = useMediaQuery('(max-width: 800px)');

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [,scrollTo] = useWindowScroll();

    useEffect(() => {
        if (opened) return;

        scrollTo({x:0, y:0});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    const icon_dict = {
        "Home": <IconHome />,
        "About": <IconUser />,
        "Kontakt": <IconAddressBook />,
        "Impressum": <IconLicense />,
    }

    const items = links.slice(0, 2).map((link) => (
        <a
            key={link.label}
            href={link.link}
            className={cx(classes.link, { [classes.linkActive]: active.link === link.link })}
            onClick={(event) => {
                // eslint-disable-next-line no-restricted-globals
                history.pushState(null, "", link.link)
                window.dispatchEvent(new Event('popstate'))
                event.preventDefault();
            }}
        >
            <Group position="left" spacing="xs">
                {/*@ts-ignore*/}
                {icon_dict[link.label]}
                {link.label}
            </Group>
        </a>
    ));



    const fullScreenItems = links.map((link) => (
        <Grid.Col span={12} key={link.label + "x"}>
            <a
                key={link.label}
                href={link.link}
                className={cx(classes.fullNavItem, { [classes.linkActive]: active === link.link })}
                onClick={(event) => {
                    // eslint-disable-next-line no-restricted-globals
                    history.pushState(null, "", link.link)
                    // @ts-ignore
                    window.dispatchEvent(new Event('popstate'))
                    toggle();
                    event.preventDefault();
                }}
            >
                <Group position="left" spacing="xs">
                    {/*@ts-ignore*/}
                    {icon_dict[link.label]}
                    {link.label}
                </Group>
            </a>
        </Grid.Col>

    ));

    const smallOverlayItems = links.map((link) => (
        <a
            key={link.label}
            href={link.link}
            className={cx(classes.link, { [classes.linkActive]: active.link === link.link })}
            onClick={(event) => {
                // eslint-disable-next-line no-restricted-globals
                history.pushState(null, "", link.link)
                // @ts-ignore
                window.dispatchEvent(new Event('popstate'))
                toggleSmallOverlay();
                event.preventDefault();
            }}
        >
            <Group position="left" spacing="xs">
                {/*@ts-ignore*/}
                {icon_dict[link.label]}
                {link.label}
            </Group>
        </a>
    ));

    return (
        <Header height={70} mb={40} style={{position:"fixed"}} key={"key0"}>
            <Grid>
                <Grid.Col span={12} md={12} lg={12} xl={12}>
                    <Container className={classes.inner}>
                        <Burger opened={opened} onClick={toggle} size="md" className={classes.burger} />
                        <Transition duration={300} timingFunction={"ease"} transition="slide-right" mounted={opened} key={"key3"}>
                            {
                                (transitionStyles) => (
                                    <Box className={classes.fullScreenNav} style={transitionStyles} key={"key1"}>
                                        <Grid mt={40} mb={40} justify="center" style={{marginLeft:"20px", marginRight:"20px", marginTop:"100px"}} key={"key2"}>
                                            {fullScreenItems}
                                        </Grid>
                                    </Box>
                                )
                            }
                        </Transition>
                        <Group className={classes.links} spacing={5}>
                            <Burger opened={openedSmallOverlay} onClick={toggleSmallOverlay} size="md" className={classes.burgerFullscreen} />
                            {items}
                        </Group>

                        <h1 style={{zIndex:"1001"}}>{settings.site_title}</h1>

                        <Group spacing={0} className={classes.social} position="right" noWrap style={{zIndex:"1001"}}>
                            {
                                isMobile ? null : (
                                    <ActionIcon onClick={() => {spotlight.open()}} size="lg">
                                        <IconSearch size={24} />
                                    </ActionIcon>
                                )
                            }
                            {
                                settings.social.twitter !== null? (
                                    /*@ts-ignore*/
                                    <ActionIcon size="lg" onClick={() => window.open(settings.social.twitter, "_blank")}>
                                        <IconBrandTwitter size="1.8rem" stroke={1.5} />
                                    </ActionIcon>
                                )   : null
                            }
                            {
                                settings.social.youtube !== null? (
                                    /*@ts-ignore*/
                                    <ActionIcon size="lg" onClick={()=> window.open(settings.social.youtube, "_blank")}>
                                        <IconBrandYoutube size="1.8rem" stroke={1.5} />
                                    </ActionIcon>
                                )   : null
                            }
                            {
                                settings.social.instagram !== null? (
                                    /*@ts-ignore*/
                                    <ActionIcon size="lg" onClick={() => window.open(settings.social.instagram, "_blank")}>
                                        <IconBrandInstagram size="1.8rem" stroke={1.5} />
                                    </ActionIcon>
                                )   : null
                            }
                        </Group>
                    </Container>
                </Grid.Col>
                <Grid.Col span={12} md={12} lg={12} xl={12}>
                    <Transition duration={200} timingFunction={"ease"} transition="scale-y" mounted={openedSmallOverlay} key={"key4"}>
                        {
                            (transitionStyles) => (
                                <Box style={transitionStyles} key={"key5"}>
                                    <Grid mt={40} mb={40} justify="center" style={{marginLeft:"20px", marginRight:"20px", marginTop:"-8px"}} key={"key6"}>
                                        <Card className={classes.smallOverlay}>
                                            {smallOverlayItems}
                                        </Card>
                                    </Grid>
                                </Box>
                            )
                        }
                    </Transition>
                </Grid.Col>
            </Grid>
        </Header>
    );
}